import React, { useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { ArrowLeft, CheckCircle2, Clock, ShieldCheck, Trash2, XCircle } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Textarea } from '@goodday-clone/ui'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import {
  useDecideDeletionRequest,
  useDeletionRequests,
  type CrmDeletionRequest,
} from '@/shared/hooks/useCrmDeletion'

// Where the superadmin says yes or no to a sales delete request. Approving is
// the moment the lead or deal is actually removed from the database; rejecting
// leaves it untouched.

function label(entityType?: string) {
  return entityType === 'lead' ? 'Lead' : 'Deal'
}

function formatValue(value?: unknown) {  const num = Number(value)
  if (!Number.isFinite(num) || num === 0) return null
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num)
}

function statusBadge(status: CrmDeletionRequest['status']) {
  if (status === 'pending') return <Badge className="gap-1.5 border-amber-500/40 bg-amber-500/10 text-xs font-semibold text-amber-600 dark:text-amber-400"><Clock className="h-3.5 w-3.5" />Awaiting approval</Badge>
  if (status === 'approved') return <Badge className="gap-1.5 border-emerald-500/40 bg-emerald-500/10 text-xs font-semibold text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />Deleted</Badge>
  if (status === 'rejected') return <Badge className="gap-1.5 border-red-500/40 bg-red-500/10 text-xs font-semibold text-red-600 dark:text-red-400"><XCircle className="h-3.5 w-3.5" />Rejected</Badge>
  return <Badge className="gap-1.5 border-border bg-muted text-xs font-semibold text-muted-foreground">Withdrawn</Badge>
}

export default function DeletionApprovalsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const mayDecide = isSuperAdminOrOwner(user?.role)
  const { data: requests = [], isLoading } = useDeletionRequests({ status: 'all' })
  const decide = useDecideDeletionRequest()

  const [approveTarget, setApproveTarget] = useState<CrmDeletionRequest | null>(null)
  const [rejectTarget, setRejectTarget] = useState<CrmDeletionRequest | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const { pending, decided } = useMemo(() => {
    const open = requests.filter((r) => r.status === 'pending')
    const history = requests
      .filter((r) => r.status !== 'pending')
      .sort((a, b) => String(b.decidedAt || b.createdAt).localeCompare(String(a.decidedAt || a.createdAt)))
    return { pending: open, decided: history.slice(0, 40) }
  }, [requests])

  const confirmApprove = async () => {
    if (!approveTarget) return
    setBusy(true)
    try {
      const res = await decide.mutateAsync({ id: approveTarget.id, action: 'approve' })
      toast.success(res?.message || `${label(approveTarget.entityType)} deleted.`)
      setApproveTarget(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'approve this delete request'))
    } finally {
      setBusy(false)
    }
  }

  const confirmReject = async () => {
    if (!rejectTarget) return
    setBusy(true)
    try {
      await decide.mutateAsync({ id: rejectTarget.id, action: 'reject', note: note.trim() || undefined })
      toast.success(`Delete request rejected. The ${label(rejectTarget.entityType)} stays.`)
      setRejectTarget(null)
      setNote('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'reject this delete request'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              Deletion Approvals
            </h1>
            <p className="text-sm text-muted-foreground">
              {mayDecide
                ? 'Sales asks, you decide. Approving removes the record from the database for good.'
                : 'Only the superadmin can decide these. Below are the delete requests you have raised.'}
            </p>
          </div>
        </div>
        {pending.length > 0 && (
          <Badge className="gap-1.5 border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
            <Clock className="h-3.5 w-3.5" />
            {pending.length} awaiting
          </Badge>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">Loading requests…</CardContent>
        </Card>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <ShieldCheck className="h-9 w-9 text-emerald-500/70" />
            <p className="text-sm font-semibold text-foreground">Nothing is waiting</p>
            <p className="text-sm text-muted-foreground">
              No lead or deal delete needs your approval right now.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {pending.map((row) => (
            <Card key={row.id} className="border-amber-500/30">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[11px] uppercase tracking-wide">
                        {row.entityType}
                      </Badge>
                      <CardTitle className="truncate text-base">{row.entityName}</CardTitle>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Requested by {row.requestedByName || 'a team member'} ·{' '}
                      {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  {statusBadge(row.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm text-foreground">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</span>
                  <p className="mt-0.5">{row.reason}</p>
                </div>
                {row.entitySummary && Object.entries(row.entitySummary).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(row.entitySummary)
                      .filter(([, v]) => v !== undefined && v !== null && v !== '')
                      .map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[11px] font-normal">
                          {k}: {k === 'value' ? (formatValue(v) ?? String(v)) : String(v)}
                        </Badge>
                      ))}
                  </div>
                )}
                {mayDecide ? (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="destructive" onClick={() => setApproveTarget(row)}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Approve delete
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setNote('')
                        setRejectTarget(row)
                      }}
                    >
                      <XCircle className="mr-1.5 h-3.5 w-3.5" />
                      Reject
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Awaiting the superadmin’s decision. You can withdraw it from the record’s menu.
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent decisions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {decided.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-2 last:border-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[11px] uppercase tracking-wide">{row.entityType}</Badge>
                    <span className="truncate text-sm font-medium">{row.entityName}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.requestedByName || 'Someone'} ·{' '}
                    {row.decidedByName ? `${row.decidedByName} ${row.status} · ` : ''}
                    {formatDistanceToNow(new Date(row.decidedAt || row.createdAt), { addSuffix: true })}
                    {row.decisionNote ? ` — ${row.decisionNote}` : ''}
                  </p>
                </div>
                {statusBadge(row.status)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={!!approveTarget}
        onCancel={() => setApproveTarget(null)}
        onConfirm={confirmApprove}
        loading={busy}
        danger
        confirmLabel="Yes, delete it"
        title={`Delete "${approveTarget?.entityName}"?`}
        message={`This is final: the ${approveTarget?.entityType} and everything recorded on it leave the database, and ${approveTarget?.requestedByName || 'the requester'} is told it happened. Reject instead to keep it.`}
      />

      <ConfirmDialog
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        onConfirm={confirmReject}
        loading={busy}
        confirmLabel="Keep the record"
        cancelLabel="Cancel"
        title={`Reject the delete request for "${rejectTarget?.entityName}"?`}
        message={`The ${rejectTarget?.entityType} stays untouched and ${rejectTarget?.requestedByName || 'the requester'} is told why.`}
      >
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Note for the requester</label>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Keep it — this account is still being worked."
            className="text-sm"
          />
        </div>
      </ConfirmDialog>
    </div>
  )
}
